import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { api } from '../../utils/api';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Users, 
  Building, 
  Phone, 
  Mail, 
  Globe,
  MapPin,
  ChevronLeft,
  ChevronRight,
  List,
  Grid,
  LayoutGrid,
  Camera,
  Upload,
  Download,
  Check,
  X,
  Loader2,
  FileSpreadsheet,
  Tag,
  MoreVertical,
  ChevronDown,
  Calendar
} from 'lucide-react';
import CustomerDeleteError from './CustomerDeleteError';
import DynamicEntityTable from '../../components/EntityManagement/DynamicEntityTable';
import SpreadsheetView from '../../components/EntityManagement/SpreadsheetView';
import EntityFilter from '../../components/EntityManagement/EntityFilter';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { getFieldOptionsSync, isSelectField as checkIsSelectField } from '../../hooks/useFieldOptions';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import ExcelImport from '../../components/ExcelImport/ExcelImport';
import BulkTagManager from '../../components/TagManagement/BulkTagManager';
import BulkFieldUpdateModal from '../../components/EntityManagement/BulkFieldUpdateModal';
import DependencyDeleteDialog from '../../components/UI/DependencyDeleteDialog';

// Helper function to render multiple emails with clickable links
const renderMultipleEmails = (emailValue, className = '') => {
  if (!emailValue || emailValue === 'null') return null;
  
  const emails = emailValue.split(';').map(e => e.trim()).filter(e => e);
  if (emails.length === 0) return null;
  
  if (emails.length === 1) {
    return (
      <a 
        href={`mailto:${emails[0]}`}
        className={`truncate hover:text-teal-600 dark:hover:text-teal-400 hover:underline transition-colors ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {emails[0]}
      </a>
    );
  }
  
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {emails.map((email, index) => (
        <a
          key={index}
          href={`mailto:${email}`}
          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/40 hover:underline transition-colors"
          onClick={(e) => e.stopPropagation()}
          title={email}
        >
          {email}
        </a>
      ))}
    </div>
  );
};

// Helper function to render multiple phone numbers
const renderMultiplePhones = (phoneValue, className = '') => {
  if (!phoneValue || phoneValue === 'null') return null;
  
  const phones = phoneValue.split(';').map(p => p.trim()).filter(p => p);
  if (phones.length === 0) return null;
  
  if (phones.length === 1) {
    return (
      <span className={`truncate ${className}`}>
        {phones[0]}
      </span>
    );
  }
  
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {phones.map((phone, index) => (
        <span
          key={index}
          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
          title={phone}
        >
          {phone}
        </span>
      ))}
    </div>
  );
};

const Customers = () => {
  const { t, i18n, ready, translationsReady, safeT } = useTranslationReady([
    'customers.title',
    'customers.person',
    'customers.company',
    'customers.addCustomer',
    'customers.searchPlaceholder'
  ]);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    config: entityConfig,
    loading: configLoading,
    getListViewFields,
    getListViewTabs
  } = useEntityConfig('customer');
  const [customers, setCustomers] = useState([]);
  const [hiddenFields, setHiddenFields] = useState([]);
  
  useEffect(() => {
    if (!entityConfig) return;
    // The entityConfig returned from useEntityConfig hook is already the config object
    const nextHiddenFields = Array.isArray(entityConfig?.hiddenFields) ? entityConfig.hiddenFields : [];
    setHiddenFields(nextHiddenFields);
    console.log('✅ Customers: Loaded hidden fields from config:', nextHiddenFields);
  }, [entityConfig]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isFieldHidden = (fieldName) => {
    return hiddenFields.includes(fieldName);
  };
  
  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]); // Array of { id, field, operator, value }
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [activeTab, setActiveTab] = useState('person'); // 'person', 'company'
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Selection state for VCF export
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [showExcelImport, setShowExcelImport] = useState(false);
  const [showBulkTagManager, setShowBulkTagManager] = useState(false);
  const [showBulkFieldUpdate, setShowBulkFieldUpdate] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [focusedCustomerId, setFocusedCustomerId] = useState(null);
  const [hasScrolledToCustomer, setHasScrolledToCustomer] = useState(false);
  const customerRefs = useRef({});
  const abortControllerRef = useRef(null);

  // Abort in-flight requests when user navigates away (releases backend pool connections)
  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return () => {
      controller.abort();
      abortControllerRef.current = null;
    };
  }, []);

  // View mode state - default to 'card' on mobile, 'list' on desktop
  const [viewMode, setViewMode] = useState(() => {
    // Check if device is mobile (screen width <= 768px)
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768 ? 'card' : 'list';
    }
    return 'list';
  }); // 'card', 'list', or 'spreadsheet'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [editingCell, setEditingCell] = useState(null); // { customerId, field }
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(false); // Track if cell is being saved
  const spreadsheetRef = useRef(null);

  const advancedFiltersKey = useMemo(() => JSON.stringify(advancedFilters), [advancedFilters]);
  const listDepsRef = useRef({ searchTerm, selectedTag, activeTab, itemsPerPage, advancedFiltersKey });

  useEffect(() => {
    const filterRelatedChanged =
      listDepsRef.current.searchTerm !== searchTerm ||
      listDepsRef.current.selectedTag !== selectedTag ||
      listDepsRef.current.activeTab !== activeTab ||
      listDepsRef.current.itemsPerPage !== itemsPerPage ||
      listDepsRef.current.advancedFiltersKey !== advancedFiltersKey;

    listDepsRef.current = { searchTerm, selectedTag, activeTab, itemsPerPage, advancedFiltersKey };

    if (filterRelatedChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    fetchCustomers();
  }, [currentPage, searchTerm, selectedTag, activeTab, itemsPerPage, advancedFiltersKey, entityConfig]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTabCounts();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedTag, advancedFiltersKey, entityConfig, activeTab]);

  // Auto-show filter bar when filters are active
  useEffect(() => {
    if (searchTerm || selectedTag || advancedFilters.length > 0) {
      setShowFilters(true);
    }
  }, [searchTerm, selectedTag, advancedFilters]);

  // Tags are loaded on demand when user opens the tag dropdown (handleTagDropdownFocus)

  // Handle customerId query parameter for scrolling to specific customer
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const customerId = urlParams.get('customerId');
    
    if (customerId && !hasScrolledToCustomer && customers.length > 0) {
      setFocusedCustomerId(parseInt(customerId));
      
      // Wait a bit for the DOM to render, then scroll to the customer
      setTimeout(() => {
        const customerRef = customerRefs.current[customerId];
        if (customerRef) {
          customerRef.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          setHasScrolledToCustomer(true);
          
          // Add a temporary highlight effect
          customerRef.style.backgroundColor = '#fef3c7';
          customerRef.style.transition = 'background-color 0.3s ease';
          setTimeout(() => {
            customerRef.style.backgroundColor = '';
          }, 2000);
        }
      }, 500);
    }
  }, [customers, location.search, hasScrolledToCustomer]);

  const configTabs = useMemo(() => {
    if (!entityConfig) return [];
    const tabs = getListViewTabs();
    return Array.isArray(tabs) ? tabs : [];
  }, [entityConfig, getListViewTabs]);

  const defaultTabs = useMemo(() => {
    const personLabel = safeT('customers.person', 'Person');
    const organizationLabel = safeT('customers.organization', 'Organization');
    return [
      {
        id: 'person',
        label: personLabel,
        filters: { type: 'Person' },
        criteria: { field: 'CustomerType', operator: 'equals', value: 'Person' },
        fields: []
      },
      {
        id: 'company',
        label: organizationLabel,
        filters: { type: 'Company' },
        criteria: { field: 'CustomerType', operator: 'equals', value: 'Company' },
        fields: []
      }
    ];
  }, [t]);

  const listTabs = configTabs.length > 0 ? configTabs : defaultTabs;
  const activeTabDef = listTabs.find(tab => tab.id === activeTab) || listTabs[0] || null;

  useEffect(() => {
    if (listTabs.length === 0) return;
    const tabIds = listTabs.map(tab => tab.id);
    if (!activeTab || !tabIds.includes(activeTab)) {
      setActiveTab(tabIds[0]);
    }
  }, [listTabs, activeTab]);

  const fetchAvailableTags = async () => {
    const signal = abortControllerRef.current?.signal;
    try {
      setTagsLoading(true);
      const response = await fetch('/api/tags?entityType=Customer', {
        signal,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableTags(data.tags || []);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Failed to fetch tags:', error);
    } finally {
      setTagsLoading(false);
    }
  };

  const handleTagDropdownFocus = () => {
    if (availableTags.length === 0 && !tagsLoading) {
      fetchAvailableTags();
    }
  };

  const fetchCustomers = async () => {
    const signal = abortControllerRef.current?.signal;
    try {
      setLoading(true);
      setError(null); // Clear previous errors

      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        sortBy: 'CreatedAt',
        sortOrder: 'DESC'
      });

      const tabFilters = activeTabDef?.filters || {};
      Object.entries(tabFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, value);
        }
      });

      if (selectedTag) {
        params.set('tag', selectedTag);
      }

      // Add advanced filters (only include valid filters with values)
      const validFilters = advancedFilters.filter(f => 
        f.field && f.operator && (f.operator === 'isNull' || f.operator === 'isNotNull' || (f.value !== undefined && f.value !== ''))
      );
      if (validFilters.length > 0) {
        params.set('filters', JSON.stringify(validFilters));
      }

      const data = await api.get(`/customers?${params.toString()}`, {
        signal,
        cacheTtlMs: 3000
      });
      if (signal?.aborted) return;
      
      // Parse Attributes JSON for each customer (moved from backend for better performance)
      const customersWithParsedAttributes = (data.customers || []).map(customer => {
        if (customer.Attributes && typeof customer.Attributes === 'string') {
          try {
            const attributes = JSON.parse(customer.Attributes);
            // Merge attributes into customer object
            return { ...customer, ...attributes };
          } catch (error) {
            console.warn('Failed to parse attributes for customer', customer.CustomerID, error);
            return customer;
          }
        }
        return customer;
      });
      
      setCustomers(customersWithParsedAttributes);
      setTotalPages(data.pagination.pages);
      setTotalCustomers(data.pagination.total);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (err?.status === 401) {
        setError(safeT('errors.unauthorized', 'Unauthorized'));
      } else if (err?.status === 403) {
        setError(safeT('errors.insufficientPermissions', 'Insufficient permissions'));
      } else {
        setError(err.message || 'Failed to fetch customers');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  // Filter customers based on active tab (now mostly handled by backend, but keep for safety)
  const filteredCustomers = useMemo(() => {
    const baseline = customers;
    if (activeTabDef?.criteria?.field) {
      const { field, operator = 'equals', value } = activeTabDef.criteria;
      return baseline.filter(customer => {
        const fieldValue = customer[field];
        if (operator === 'equals') {
          return fieldValue === value;
        }
        if (operator === 'notEquals') {
          return fieldValue !== value;
        }
        if (operator === 'in' && Array.isArray(value)) {
          return value.includes(fieldValue);
        }
        return true;
      });
    }
    // Fallback to legacy behaviour
    if (activeTab === 'person') {
      return baseline.filter(customer => customer.CustomerType === 'Person');
    }
    if (activeTab === 'company') {
      return baseline.filter(customer => customer.CustomerType === 'Company');
    }
    return baseline;
  }, [customers, activeTabDef, activeTab]);

  // Backend already paginates; keep alias for stable call sites and memoized access.
  const paginatedCustomers = useMemo(() => filteredCustomers, [filteredCustomers]);

  // Get counts for each tab - need to fetch separately for accurate counts
  const [tabCounts, setTabCounts] = useState({});
  
  // Fetch counts for both tabs separately (memoized to prevent unnecessary re-creation)
  const fetchTabCounts = React.useCallback(async () => {
    if (listTabs.length === 0) {
      setTabCounts({});
      return;
    }
    try {
      const baseParams = {
        page: 1,
        limit: 1,
        search: searchTerm,
        sortBy: 'CreatedAt',
        sortOrder: 'DESC'
      };

      const results = await Promise.all(
        listTabs.map(async (tab) => {
          const params = new URLSearchParams(baseParams);

          Object.entries(tab.filters || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              params.set(key, value);
            }
          });

          if (selectedTag) {
            params.set('tag', selectedTag);
          }

          const validFilters = advancedFilters.filter(f =>
            f.field && f.operator && (f.operator === 'isNull' || f.operator === 'isNotNull' || (f.value !== undefined && f.value !== ''))
          );
          if (validFilters.length > 0) {
            params.set('filters', JSON.stringify(validFilters));
          }
          const data = await api.get(`/customers?${params.toString()}`, {
            cacheTtlMs: 5000,
            dedupe: true
          });
          return { id: tab.id, count: data.pagination?.total || 0 };
        })
      );

      const counts = results.reduce((acc, item) => {
        acc[item.id] = item.count;
        return acc;
      }, {});

      setTabCounts(counts);
    } catch (err) {
      console.error('Failed to fetch tab counts:', err);
      setTabCounts({});
    }
  }, [listTabs, searchTerm, selectedTag, advancedFilters]);

  const handleCreateCustomer = () => {
    navigate('/customers/new');
  };

  // Handle VCF Export
  const handleExportVCF = async () => {
    // If no customers are selected, show the selection modal
    if (selectedCustomers.size === 0) {
      setShowExportModal(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const customerIds = Array.from(selectedCustomers);
      const params = new URLSearchParams({
        customerIds: customerIds.join(',')
      });
      
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/customers/export/vcf?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to export VCF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_${new Date().toISOString().split('T')[0]}.vcf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      // Clear selection after successful export
      setSelectedCustomers(new Set());
      setSelectAll(false);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customerId) => {
    const newSelected = new Set(selectedCustomers);
    if (newSelected.has(customerId)) {
      newSelected.delete(customerId);
    } else {
      newSelected.add(customerId);
    }
    setSelectedCustomers(newSelected);
    
    // Update select all state
    if (newSelected.size === paginatedCustomers.length) {
      setSelectAll(true);
    } else if (newSelected.size === 0) {
      setSelectAll(false);
    } else {
      setSelectAll(false);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCustomers(new Set());
      setSelectAll(false);
    } else {
      const allCustomerIds = paginatedCustomers.map(customer => customer.CustomerID);
      setSelectedCustomers(new Set(allCustomerIds));
      setSelectAll(true);
    }
  };

  // Handle export with selected customers
  const handleExportSelected = async () => {
    if (selectedCustomers.size === 0) {
      setError('Please select at least one customer to export');
      return;
    }

    setShowExportModal(false);
    await handleExportVCF();
  };

  // Handle bulk export to Excel
  const handleBulkExportExcel = async () => {
    if (selectedCustomers.size === 0) {
      toast.error('Please select at least one customer to export');
      return;
    }

    try {
      setLoading(true);
      
      const customerIds = Array.from(selectedCustomers);
      const params = new URLSearchParams({
        customerIds: customerIds.join(',')
      });
      
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/customers/export/excel?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to export Excel');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${selectedCustomers.size} customer${selectedCustomers.size !== 1 ? 's' : ''} to Excel`);
      
      // Clear selection after successful export
      setSelectedCustomers(new Set());
      setSelectAll(false);
      
    } catch (err) {
      toast.error(err.message || 'Failed to export to Excel');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedCustomers.size === 0) {
      toast.error('Please select at least one customer to delete');
      return;
    }

    const count = selectedCustomers.size;
    if (!window.confirm(
      safeT(
        'customers.confirmBulkDelete', 
        `Are you sure you want to delete ${count} customer${count !== 1 ? 's' : ''}? This action cannot be undone.`
      )
    )) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const customerIds = Array.from(selectedCustomers);
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch('/api/customers/bulk-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        body: JSON.stringify({ customerIds })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete customers');
      }

      const result = await response.json();
      
      toast.success(
        `Successfully deleted ${result.deleted || count} customer${result.deleted !== 1 ? 's' : ''}` +
        (result.failed ? `. ${result.failed} failed.` : '')
      );
      
      // Clear selection
      setSelectedCustomers(new Set());
      setSelectAll(false);
      
      // Refresh the list
      fetchCustomers();
      
    } catch (err) {
      toast.error(err.message || 'Failed to delete customers');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk tag manager success
  const handleBulkTagSuccess = () => {
    // Refresh the customer list to show updated tags
    fetchCustomers();
    // Refresh the available tags list in case new tags were created
    fetchAvailableTags();
  };

  // Handle bulk field update success
  const handleBulkFieldUpdateSuccess = () => {
    // Refresh the customer list to show updated fields
    fetchCustomers();
    // Refresh tab counts to update Person/Company counts
    fetchTabCounts();
  };

  // Handle VCF Import
  const handleImportVCF = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.vcf,.vcard';
    input.multiple = true;
    
    input.onchange = async (e) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      try {
        setLoading(true);
        setError(null);
        
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append('vcfFiles', files[i]);
        }

        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch('/api/customers/import/vcf', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to import VCF');
        }

        const result = await response.json();
        alert(`Import completed! ${result.imported || 0} contacts imported, ${result.skipped || 0} skipped.`);
        fetchCustomers(); // Refresh the customer list
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    input.click();
  };

  const handleEditCustomer = (customer) => {
    if (!customer?.CustomerID) return;
    navigate(`/customers/${customer.CustomerID}/edit`);
  };

  const handleViewCustomer = (customer) => {
    if (!customer?.CustomerID) return;
    navigate(`/customers/${customer.CustomerID}`);
  };

  // Open company detail by CompanyID (maps to its CustomerID first)
  const handleViewCompanyByCompanyId = async (companyId, companyName) => {
    if (!companyId) {
      console.warn('⚠️ handleViewCompanyByCompanyId called with empty companyId:', { companyId, companyName });
      if (companyName) {
        // If we have a company name but no ID, search by name
        setActiveTab('company');
        setSearchTerm(companyName);
        setCurrentPage(1);
        toast.info('Searching for company by name...');
      }
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Looking up company by ID:', { companyId, companyName });

      // Find the company customerId from the Companies table mapping
      const selectedRole = localStorage.getItem('selectedRole');
      const mapRes = await fetch(`/api/customers/company/${companyId}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });
      if (!mapRes.ok) {
        // Fallback: if no dedicated company customer exists, try name-based search
        if (mapRes.status === 404 && companyName) {
          const params = new URLSearchParams({
            page: 1,
            limit: 10,
            search: companyName,
            type: 'Company',
            sortBy: 'CreatedAt',
            sortOrder: 'DESC'
          });
          const listRes = await fetch(`/api/customers?${params}`, {
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
            }
          });
          if (listRes.ok) {
            const listData = await listRes.json();
            const exact = (listData.customers || []).find(c => (c.CompanyName || '').trim() === companyName.trim());
            const candidate = exact || (listData.customers || [])[0];
            if (candidate) {
              navigate(`/customers/${candidate.CustomerID}`);
              toast((t) => (
                <span>
                  Company record not linked by ID; opened closest match by name.
                </span>
              ), { id: 'company-fallback-opened', duration: 4000 });
              return;
            }
          }
          // If still nothing, switch to Companies tab with prefilled search
          setActiveTab('company');
          setSearchTerm(companyName);
          setCurrentPage(1);
          toast.error('Company customer not found by ID. Showing companies matching the name.');
          return;
        }
        const errData = await mapRes.json().catch(() => ({}));
        throw new Error(errData.message || 'Company customer not found');
      }
      const mapData = await mapRes.json();
      console.log('✅ Company lookup response:', mapData);

      // Check if company exists as a customer
      if (mapData.exists && mapData.customer) {
        // Company exists as a Customer record, navigate directly
        console.log('✅ Company exists as customer, navigating to:', mapData.customer.CustomerID);
        navigate(`/customers/${mapData.customer.CustomerID}`);
      } else if (mapData.company) {
        // Company exists in Companies table but not as Customer yet
        // Switch to Companies tab with search
        console.log('ℹ️ Company exists but not as customer yet, searching by name');
        setActiveTab('company');
        setSearchTerm(companyName);
        setCurrentPage(1);
        toast.info('Company found in database but not yet a customer. Showing companies matching the name.');
      } else {
        console.error('❌ Unexpected response structure:', mapData);
        throw new Error('Company customer record missing CustomerID');
      }
    } catch (err) {
      console.error('❌ Failed to open company details:', err);
      toast.error(err.message || 'Failed to open company details');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    customerId: null,
    dependencies: null
  });

  const handleDeleteCustomer = async (customerId, confirmDeleteDependencies = false) => {
    // If not confirming dependencies, show initial confirmation
    if (!confirmDeleteDependencies) {
      if (!window.confirm(safeT('common.confirmDelete', 'Are you sure you want to delete this item?'))) {
        return;
      }
    }

    try {
      const selectedRole = localStorage.getItem('selectedRole');
      const url = `/api/customers/${customerId}${confirmDeleteDependencies ? '?confirmDeleteDependencies=true' : ''}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Check if this is a dependency confirmation required response
        if (response.status === 409 && responseData.requiresConfirmation) {
          setDeleteDialog({
            isOpen: true,
            customerId: customerId,
            dependencies: responseData.dependencies
          });
          return;
        }

        let errorMessage = 'Failed to delete customer';
        
        if (response.status === 401) {
          errorMessage = safeT('errors.unauthorized', 'Unauthorized');
        } else if (response.status === 403) {
          errorMessage = safeT('errors.insufficientPermissions', 'Insufficient permissions');
        } else if (response.status === 404) {
          errorMessage = safeT('errors.customerNotFound', 'Customer not found');
        } else if (response.status === 400) {
          errorMessage = responseData.message || safeT('errors.customerDeleteWithRelationships', 'Cannot delete customer with existing relationships');
        } else if (responseData.message) {
          errorMessage = responseData.message;
        }
        
        throw new Error(errorMessage);
      }

      fetchCustomers();
      setError(null);
      setDeleteDialog({ isOpen: false, customerId: null, dependencies: null });
      
      if (responseData.deletedDependencies) {
        toast.success(`Customer deleted successfully. Also deleted: ${responseData.deletedDependencies.join(', ')}`);
      } else {
        toast.success('Customer deleted successfully');
      }
    } catch (err) {
      setError(err.message);
      setDeleteDialog({ isOpen: false, customerId: null, dependencies: null });
    }
  };

  const handleConfirmBulkDeleteWithDependencies = () => {
    if (deleteDialog.isBulk && deleteDialog.entityIds) {
      handleBulkDelete(true);
    }
  };

  const handleConfirmDeleteWithDependencies = () => {
    if (deleteDialog.isBulk) {
      handleConfirmBulkDeleteWithDependencies();
    } else if (deleteDialog.customerId) {
      handleDeleteCustomer(deleteDialog.customerId, true);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCustomers();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(parseInt(newItemsPerPage));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const formatCustomerName = (customer) => {
    // First line only: Name (Nickname) for Person, CompanyName for Company
    if (customer.CustomerType === 'Person') {
      // Format: Name (Nickname) - JobTitle will be on second line
      const nameParts = [];
      if (customer.FirstName) nameParts.push(customer.FirstName);
      if (customer.LastName) nameParts.push(customer.LastName);
      
      let nameSection = nameParts.join(' ').trim();
      
      // Add nickname in parentheses if available
      if (customer.NickName && nameSection) {
        nameSection += ` (${customer.NickName})`;
      } else if (customer.NickName && !nameSection) {
        nameSection = customer.NickName;
      }
      
      return nameSection || '-';
    } else {
      // Format: CompanyName only - Branch will be on second line
      return customer.CompanyName || customer.CompanyName_Eng || '-';
    }
  };

  const formatCustomerInfo = (customer) => {
    // Second line: JobTitle for Person, Branch for Company
    if (customer.CustomerType === 'Person') {
      return customer.JobTitle || safeT('customers.individual', 'Person');
    } else {
      return customer.Branch || customer.Industry || safeT('customers.company', 'Organization');
    }
  };

  const formatCustomerContact = (customer) => {
    if (customer.CustomerType === 'Person') {
      const email = customer.PersonEmail && customer.PersonEmail !== 'null' ? customer.PersonEmail : '';
      const phone = customer.PersonPhone && customer.PersonPhone !== 'null' ? customer.PersonPhone : '';
      // For multiple values, show first one with count
      if (email) {
        const emails = email.split(';').filter(e => e.trim());
        return emails.length > 1 ? `${emails[0]} (+${emails.length - 1})` : emails[0];
      }
      if (phone) {
        const phones = phone.split(';').filter(p => p.trim());
        return phones.length > 1 ? `${phones[0]} (+${phones.length - 1})` : phones[0];
      }
      return '-';
    } else {
      const website = customer.Website && customer.Website !== 'null' ? customer.Website : '';
      const address = customer.CompanyAddress && customer.CompanyAddress !== 'null' ? customer.CompanyAddress : '';
      return website || address || '-';
    }
  };

  const formatCustomerPhone = (customer) => {
    if (customer.CustomerType === 'Person') {
      const phone = customer.PersonPhone && customer.PersonPhone !== 'null' ? customer.PersonPhone : '';
      if (phone) {
        const phones = phone.split(';').filter(p => p.trim());
        return phones.length > 1 ? `${phones[0]} (+${phones.length - 1})` : phones[0];
      }
      return '-';
    } else {
      const phone = customer.Phone && customer.Phone !== 'null' ? customer.Phone : '';
      if (phone) {
        const phones = phone.split(';').filter(p => p.trim());
        return phones.length > 1 ? `${phones[0]} (+${phones.length - 1})` : phones[0];
      }
      return '-';
    }
  };

  const formatSalesPerson = (customer) => {
    // Check for salesperson fields from the backend
    if (customer.SalespersonFirstName && customer.SalespersonLastName) {
      return `${customer.SalespersonFirstName} ${customer.SalespersonLastName}`;
    } else if (customer.SalespersonFirstName) {
      return customer.SalespersonFirstName;
    } else if (customer.SalespersonLastName) {
      return customer.SalespersonLastName;
    }
    return '-';
  };

  // Spreadsheet view functions
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortableValue = (customer, columnKey) => {
    // Handle special virtual fields
    if (columnKey === 'CombinationName') {
      return formatCustomerName(customer).toLowerCase();
    }
    
    // Get the raw value from customer
    const rawValue = customer[columnKey];
    
    // Find field config
    const fieldConfig = entityConfig?.fields?.[columnKey];
    
    // Handle numeric fields
    if (fieldConfig?.dataType === 'integer' || fieldConfig?.dataType === 'number' || fieldConfig?.dataType === 'decimal') {
      return Number(rawValue) || 0;
    }
    
    // Handle date fields
    if (fieldConfig?.dataType === 'date' || fieldConfig?.dataType === 'datetime') {
      return rawValue ? new Date(rawValue).getTime() : 0;
    }
    
    // Handle boolean fields
    if (fieldConfig?.dataType === 'boolean') {
      return rawValue ? 1 : 0;
    }
    
    // Default: string sorting (case-insensitive)
    return String(rawValue || '').toLowerCase();
  };

  const getSortedCustomers = () => {
    const filtered = filteredCustomers;
    if (!sortConfig.key) return filtered;

    const fieldConfig = entityConfig?.fields?.[sortConfig.key];
    const isNumeric = fieldConfig?.dataType === 'integer' || 
                      fieldConfig?.dataType === 'number' || 
                      fieldConfig?.dataType === 'decimal';

    return [...filtered].sort((a, b) => {
      const aVal = getSortableValue(a, sortConfig.key);
      const bVal = getSortableValue(b, sortConfig.key);
      
      // Handle numeric/date sorting (already numbers from getSortableValue)
      if (isNumeric || fieldConfig?.dataType === 'date' || fieldConfig?.dataType === 'datetime') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string sorting
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleCopyToClipboard = () => {
    const sorted = getSortedCustomers();
    
    // Build headers from spreadsheet columns
    const headers = spreadsheetColumns.map(col => col.label);
    
    // Build rows using the same getSpreadsheetCellValue function
    const rows = sorted.map(customer => 
      spreadsheetColumns.map(col => 
        getSpreadsheetCellValue(customer, col.key, col.fieldConfig)
      )
    );

    const tsvContent = [headers, ...rows]
      .map(row => row.join('\t'))
      .join('\n');

    navigator.clipboard.writeText(tsvContent).then(() => {
      toast.success('Customer data copied to clipboard! You can now paste it into Excel or Google Sheets.', {
        duration: 4000,
        icon: '📋'
      });
    }).catch(err => {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy to clipboard');
    });
  };

  const handleCellEdit = (customerId, field, value) => {
    setEditingCell({ customerId, field });
    setEditingValue(value || '');
  };

  const handleSaveCellEdit = async () => {
    if (!editingCell) return;

    const { customerId, field } = editingCell;
    const customer = customers.find(c => c.CustomerID === customerId);
    
    if (!customer) {
      setEditingCell(null);
      return;
    }

    // If value hasn't changed, just cancel
    if (customer[field] === editingValue) {
      setEditingCell(null);
      setEditingValue('');
      return;
    }

    try {
      setSavingCell(true);
      
      console.log('🔄 Updating customer field:', { customerId, field, value: editingValue });

      // Use the smart PATCH endpoint that uses entity config mapping
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/customers/${customerId}/field`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        body: JSON.stringify({
          fieldName: field,
          fieldValue: editingValue
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update customer');
      }

      const responseData = await response.json();
      console.log('✅ Customer field updated successfully:', responseData);

      // Clear editing state first to exit edit mode
      setEditingCell(null);
      setEditingValue('');

      // Update the customer in the local state with the complete data from backend
      if (responseData.success && responseData.data) {
        console.log('📊 Updating local state with backend data:', {
          customerId,
          fieldName: field,
          fieldValue: responseData.data[field],
          fullData: responseData.data
        });

        // Update state with the complete customer data from backend
        setCustomers(prevCustomers => 
          prevCustomers.map(c => 
            c.CustomerID === customerId ? responseData.data : c
          )
        );
        
        toast.success(responseData.message || 'Field updated successfully');
      } else {
        // Fallback: update just the field directly if backend doesn't return data
        console.warn('⚠️ Backend did not return updated customer data, using fallback');
        setCustomers(prevCustomers => 
          prevCustomers.map(c => 
            c.CustomerID === customerId ? { ...c, [field]: editingValue } : c
          )
        );
        toast.success('Field updated successfully');
      }
    } catch (err) {
      console.error('❌ Failed to update customer:', err);
      toast.error('Failed to update: ' + err.message);
    } finally {
      setSavingCell(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  // Helper function to get field display name in current language
  const getFieldDisplayName = (fieldName, fallback) => {
    if (!entityConfig || !entityConfig.fields || !entityConfig.fields[fieldName]) {
      return fallback;
    }
    const field = entityConfig.fields[fieldName];
    const isThaiLanguage = i18n?.language?.toLowerCase().startsWith('th') || false;
    return isThaiLanguage && field.displayNameTH 
      ? field.displayNameTH 
      : (field.displayName || fallback);
  };

  // Build spreadsheet columns from the same source as list view
  const spreadsheetColumns = useMemo(() => {
    const fields = getListViewFields({ tabName: activeTab });
    if (!fields || fields.length === 0) {
      // Fallback to basic columns if no config available
      return [
        { key: 'CustomerID', label: 'ID', width: '80px', editable: false, fieldName: 'CustomerID' },
        { key: 'FirstName', label: 'Name', width: '200px', editable: false, fieldName: 'FirstName' }
      ];
    }
    
    // Determine if current language is Thai
    const isThaiLanguage = i18n?.language?.toLowerCase().startsWith('th') || false;
    
    // Convert list view fields to spreadsheet columns
    return fields.map(field => {
      const fieldConfig = entityConfig?.fields?.[field.name];
      const dbTable = fieldConfig?.database?.table;
      
      // Determine if field is editable (only direct table columns, not joined fields)
      const isEditable = fieldConfig && (
        (activeTab === 'person' && dbTable === 'Persons') ||
        (activeTab === 'company' && dbTable === 'Companies') ||
        (dbTable === 'Customers')
      );
      
      return {
        key: field.name,
        label: isThaiLanguage && field.displayNameTH 
          ? field.displayNameTH 
          : (field.displayName || field.name),
        width: field.width || '150px',
        editable: isEditable,
        fieldName: field.name,
        fieldConfig: fieldConfig
      };
    });
  }, [activeTab, getListViewFields, entityConfig, i18n?.language]);

  // Helper to get display label for select field values
  const getSelectFieldLabel = (fieldName, value) => {
    if (!value || !entityConfig) return value || '-';
    
    const fieldOptions = getFieldOptionsSync(entityConfig, fieldName);
    if (fieldOptions.length === 0) return value;
    
    const option = fieldOptions.find(opt => opt.value === value);
    return option ? (option.label || option.value) : value;
  };

  const getSpreadsheetCellValue = (customer, columnKey, fieldConfig) => {
    // Handle special virtual fields
    if (columnKey === 'CombinationName') {
      return formatCustomerName(customer);
    }
    
    // Handle SalespersonID - show full name instead of ID
    if (columnKey === 'SalespersonID') {
      return formatSalesPerson(customer);
    }
    
    // Get the raw value from customer object
    const rawValue = customer[columnKey];
    
    // Handle null/undefined
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }
    
    // Check if this is a select field and get label
    if (fieldConfig && checkIsSelectField(entityConfig, columnKey)) {
      return getSelectFieldLabel(columnKey, rawValue);
    }
    
    // Format based on data type
    if (fieldConfig?.dataType === 'date' && rawValue) {
      try {
        const date = new Date(rawValue);
        return date.toLocaleDateString();
      } catch {
        return rawValue;
      }
    }
    
    if (fieldConfig?.dataType === 'datetime' && rawValue) {
      try {
        const date = new Date(rawValue);
        return date.toLocaleString();
      } catch {
        return rawValue;
      }
    }
    
    // Return the raw value as string
    return String(rawValue);
  };

  const getEditableField = (customer, columnKey, fieldConfig) => {
    // If no field config, it's not editable
    if (!fieldConfig) return null;
    
    const dbTable = fieldConfig.database?.table;
    
    // Check if field is from the correct table for this customer type
    if (customer.CustomerType === 'Person' && dbTable === 'Persons') {
      return columnKey; // Editable for Person customers
    }
    
    if (customer.CustomerType === 'Company' && dbTable === 'Companies') {
      return columnKey; // Editable for Company customers
    }
    
    if (dbTable === 'Customers') {
      return columnKey; // Always editable (base table)
    }
    
    // Custom attributes are always editable
    if (fieldConfig.storage?.type === 'attributes') {
      return columnKey;
    }
    
    // Fields from joined tables are not editable
    return null;
  };

  const tableColumns = useMemo(() => {
    const fields = getListViewFields({ tabName: activeTab });
    if (!fields || fields.length === 0) return null;
    
    // Determine if current language is Thai
    const isThaiLanguage = i18n?.language?.toLowerCase().startsWith('th') || false;
    
    return fields.map(field => ({
      key: field.name,
      label: isThaiLanguage && field.displayNameTH 
        ? field.displayNameTH 
        : (field.displayName || field.name),
      field,
      sortable: true,
      visible: true
    }));
  }, [activeTab, getListViewFields, i18n?.language]);

  const activeTabLabel = activeTabDef?.label || safeT('nav.customers', safeT('customers.customers', 'Customers'));

  const showTranslationLoading = !translationsReady || !ready;

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {showTranslationLoading && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Loading translations...
        </div>
      )}
      {/* Header - Title and Actions in Same Line */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        {/* Title Section */}
        <div className="flex-shrink-0">
          <div className="flex items-center space-x-3">
            <img 
              src="icon/lead.png" 
              alt="Customers Icon" 
              className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain"
            />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{safeT('nav.customers', 'Customers')}</h1>
          </div>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-300 mt-1">{safeT('customers.subtitle', 'Manage your customer database')}</p>
        </div>
        
          {/* Action Buttons - Collapsible Menu */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
          {/* View Mode Toggle - Always Visible */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-full sm:w-auto">
            <button
              onClick={() => setViewMode('card')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'card'
                  ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Card View"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">Card</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="List View"
            >
              <List className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">List</span>
            </button>
            <button
              onClick={() => setViewMode('spreadsheet')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'spreadsheet'
                  ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Spreadsheet View"
            >
              <Grid className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">Spreadsheet</span>
            </button>
          </div>
          
          {/* Filter Button - Between View Toggle and Action Buttons */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight transition-all ${
              searchTerm || selectedTag
                ? 'bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-700'
                : showFilters
                ? 'bg-gray-600 dark:bg-gray-500 text-white hover:bg-gray-700 dark:hover:bg-gray-600'
                : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-700'
            }`}
            title="Toggle filters"
          >
            <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
            <span className="hidden sm:inline">{safeT('common.filter', 'Filter')}</span>
            {(searchTerm || selectedTag) && (
              <span className="bg-white/20 px-1 sm:px-1.5 py-0.5 rounded-full text-xs font-bold">
                {(searchTerm ? 1 : 0) + (selectedTag ? 1 : 0)}
              </span>
            )}
          </button>
          
          {/* Action Buttons Row */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Expandable Menu Buttons - Collapsible on both mobile and desktop */}
            <div className={`flex flex-wrap items-center gap-2 ${menuExpanded ? 'flex' : 'hidden'}`}>
              {viewMode === 'spreadsheet' && (
                <button
                  onClick={handleCopyToClipboard}
                  className="bg-green-600 dark:bg-green-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight"
                  title="Copy to Clipboard"
                >
                  <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Copy</span>
                </button>
              )}
              
              <button
                onClick={handleImportVCF}
                className="bg-purple-600 dark:bg-purple-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight"
                title={safeT('customers.importVCF', 'Import VCF')}
              >
                <Upload className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="hidden md:inline">{safeT('customers.importVCF', 'Import VCF')}</span>
                <span className="md:hidden">Import</span>
              </button>
              
              <button
                onClick={handleExportVCF}
                className="bg-orange-600 dark:bg-orange-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-orange-700 dark:hover:bg-orange-600 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight"
                title={safeT('customers.exportVCF', 'Export VCF')}
              >
                <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="hidden md:inline">
                  {selectedCustomers.size > 0 
                    ? `${safeT('customers.exportVCF', 'Export VCF')} (${selectedCustomers.size})` 
                    : safeT('customers.exportVCF', 'Export VCF')
                  }
                </span>
                <span className="md:hidden">
                  Export{selectedCustomers.size > 0 ? ` (${selectedCustomers.size})` : ''}
                </span>
              </button>
              
              <button
                onClick={() => navigate('/business-card-scanner')}
                className="bg-green-600 dark:bg-green-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight"
                title="Business Card Scanner"
              >
                <Camera className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="hidden lg:inline">{safeT('nav.businessCardScanner', 'Business Card Scanner')}</span>
                <span className="lg:hidden">Scan Card</span>
              </button>
              
              <button
                onClick={() => setShowExcelImport(true)}
                className="bg-green-600 dark:bg-green-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight"
              >
                <FileSpreadsheet className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="hidden sm:inline">{safeT('customers.importExcel', 'Import Excel')}</span>
                <span className="sm:hidden">Import</span>
              </button>
            </div>
            
            {/* Expand/Collapse Button - Visible on all screen sizes */}
            <button
              onClick={() => setMenuExpanded(!menuExpanded)}
              className="flex bg-gray-500 dark:bg-gray-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-gray-600 dark:hover:bg-gray-700 items-center justify-center space-x-1 text-xs sm:text-sm touch-target no-tap-highlight transition-transform"
              title={menuExpanded ? 'Collapse menu' : 'Expand menu'}
            >
              <MoreVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
              <ChevronDown className={`h-3 w-3 flex-shrink-0 transition-transform duration-200 ${menuExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Add Customer Button - Always Visible */}
            <button
              onClick={handleCreateCustomer}
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 dark:from-teal-600 dark:to-cyan-600 dark:hover:from-teal-700 dark:hover:to-cyan-700 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold touch-target no-tap-highlight shadow-md transition-all duration-200"
            >
              <Plus className="h-5 w-5 flex-shrink-0" />
              <span>{safeT('customers.addCustomer', 'Add Customer')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters - Mobile Optimized - Hidden by Default */}
      {showFilters && (
        <div className="space-y-4">
          {/* Quick Search and Tag Filter */}
          <div className={`p-3 sm:p-4 rounded-lg shadow border transition-colors duration-200 ${
            searchTerm || selectedTag
              ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}>
            {(searchTerm || selectedTag) && (
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                  🔍 Quick Filters Active
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedTag('');
                  }}
                  className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
                >
                  Clear Quick Filters
                </button>
              </div>
            )}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4">
              <div className="flex-1 w-full sm:min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 sm:top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder={safeT('customers.searchPlaceholder', 'Search customers...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-teal-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  onFocus={handleTagDropdownFocus}
                  className="w-full sm:w-auto px-2 sm:px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-teal-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Filter by tag"
                >
                  <option value="">{safeT('customers.allTags', 'All Tags')}</option>
                  {availableTags && availableTags.length > 0 ? (
                    availableTags.map((tag) => (
                      <option key={tag.id || tag.name} value={tag.name}>
                        {tag.name} ({tag.usageCount || 0})
                      </option>
                    ))
                  ) : tagsLoading ? (
                    <option value="" disabled>Loading tags...</option>
                  ) : null}
                </select>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="w-full sm:w-auto px-2 sm:px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-teal-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Items per page"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <button
                  type="submit"
                  className="col-span-2 sm:col-span-1 bg-gray-600 dark:bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center justify-center space-x-1 sm:space-x-2 text-sm sm:text-base touch-target no-tap-highlight"
                >
                  <Filter className="h-4 w-4" />
                  <span>{safeT('common.filter', 'Filter')}</span>
                </button>
              </div>
            </form>
          </div>

          {/* Advanced Filters */}
          <EntityFilter
            entityName="customer"
            filters={advancedFilters}
            onFiltersChange={(filters) => {
              setAdvancedFilters(filters);
              setCurrentPage(1); // Reset to first page when filters change
            }}
            customerType={activeTab === 'person' ? 'Person' : activeTab === 'company' ? 'Company' : null}
          />
        </div>
      )}

      {/* Tab Navigation (Persons / Organizations) - Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <nav className="flex space-x-2 sm:space-x-4 md:space-x-8 px-2 sm:px-4 md:px-6 overflow-x-auto scroll-smooth-mobile flex-1" aria-label="Tabs">
              {listTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap touch-target ${
                    activeTab === tab.id
                      ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {tab.icon === 'Building' || (!tab.icon && tab.id === 'company') ? (
                    <Building className="h-4 w-4 flex-shrink-0" />
                  ) : (
                    <Users className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span>{tab.label}</span>
                  <span className="ml-1 sm:ml-2 bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 py-0.5 px-1.5 sm:px-2.5 rounded-full text-xs font-medium">
                    {tabCounts[tab.id] ?? 0}
                  </span>
                </button>
              ))}
            </nav>
            
            {/* Bulk Action Buttons - On the right side of tabs */}
            {selectedCustomers.size > 0 && (
              <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 flex-shrink-0">
                <div className="bg-teal-100 dark:bg-teal-800 border border-teal-200 dark:border-teal-600 rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 mr-1 sm:mr-2">
                  <span className="text-xs sm:text-sm text-teal-700 dark:text-teal-300 font-medium whitespace-nowrap">
                    {selectedCustomers.size} selected
                  </span>
                </div>
                <button
                  onClick={() => setShowBulkTagManager(true)}
                  disabled={loading}
                  className="bg-purple-600 dark:bg-purple-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight"
                  title="Manage tags for selected customers"
                >
                  <Tag className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{safeT('customers.manageTags', 'Manage Tags')}</span>
                </button>
                
                <button
                  onClick={() => setShowBulkFieldUpdate(true)}
                  disabled={loading}
                  className="bg-blue-600 dark:bg-blue-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight"
                  title="Update field values for selected customers"
                >
                  <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{safeT('customers.updateFields', 'Update Fields')}</span>
                </button>
                
                <button
                  onClick={handleBulkExportExcel}
                  disabled={loading}
                  className="bg-green-600 dark:bg-green-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight"
                  title="Export selected to Excel"
                >
                  <FileSpreadsheet className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{safeT('customers.exportExcel', 'Export Excel')}</span>
                </button>
                
                <button
                  onClick={handleBulkDelete}
                  disabled={loading}
                  className="bg-red-600 dark:bg-red-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight"
                  title="Delete selected customers"
                >
                  <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{safeT('customers.bulkDelete', 'Delete')}</span>
                </button>
                
                <button
                  onClick={() => {
                    setSelectedCustomers(new Set());
                    setSelectAll(false);
                  }}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 touch-target"
                  title="Clear selection"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <CustomerDeleteError 
          error={error} 
          onRetry={() => {
            setError(null);
            fetchCustomers();
          }} 
        />
      )}

      {/* Results Summary - Mobile Optimized */}
      <div className="flex justify-between items-center">
        <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-300">
          <span className="hidden sm:inline">Showing </span>
          <span className="font-medium">{totalCustomers > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span>
          <span className="hidden xs:inline"> to </span>
          <span className="xs:hidden">-</span>
          <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalCustomers)}</span>
          <span className="hidden xs:inline"> of </span>
          <span className="xs:hidden">/</span>
          <span className="font-medium">{totalCustomers}</span>
          <span className="hidden sm:inline"> {activeTabLabel}</span>
        </p>
      </div>

      {/* Customer List, Card, or Spreadsheet View */}
      {viewMode === 'card' ? (
        /* Card/Gallery View */
        <div className="space-y-4">
          {loading || configLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
                  <div className="flex items-start space-x-3">
                    <div className="h-12 w-12 bg-gray-200 dark:bg-gray-600 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : paginatedCustomers.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <Users className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {safeT('customers.noCustomers', 'No customers found')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {searchTerm || selectedTag
                  ? safeT('customers.noCustomersFiltered', 'Try adjusting your filters')
                  : safeT('customers.noCustomersDesc', 'Get started by adding your first customer')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {paginatedCustomers.map((customer) => {
                const isSelected = selectedCustomers.has(customer.CustomerID);
                const isCompany = customer.CustomerType === 'Company';
                
                return (
                  <div
                    key={customer.CustomerID}
                    ref={(el) => {
                      if (el) customerRefs.current[customer.CustomerID] = el;
                    }}
                    className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md ${
                      isSelected
                        ? 'border-teal-500 dark:border-blue-400 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    } ${isCompany ? 'ring-1 ring-blue-200 dark:ring-blue-800' : ''}`}
                  >
                    {/* Card Header */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3 flex-1 min-w-0">
                          {/* Avatar */}
                          <div className={`flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center ${
                            isCompany
                              ? 'bg-teal-100 dark:bg-teal-900'
                              : customer.parentCompany
                              ? 'bg-green-100 dark:bg-green-900'
                              : 'bg-gray-100 dark:bg-gray-700'
                          }`}>
                            {isCompany ? (
                              <Building className="h-6 w-6 text-blue-600 dark:text-blue-300" />
                            ) : (
                              <Users className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                            )}
                          </div>
                          
                          {/* Name and Info */}
                          <div className="flex-1 min-w-0">
                            <h3
                              className={`text-sm font-semibold truncate cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors ${
                                isCompany
                                  ? 'text-blue-900 dark:text-blue-100'
                                  : 'text-gray-900 dark:text-white'
                              }`}
                              onClick={() => handleViewCustomer(customer)}
                              title="Click to view details"
                            >
                              {formatCustomerName(customer)}
                            </h3>
                            <p className={`text-xs mt-1 truncate ${
                              isCompany
                                ? 'text-blue-700 dark:text-blue-300'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {formatCustomerInfo(customer)}
                            </p>
                            {customer.parentCompany && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                                at {customer.parentCompany}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons and Selection Checkbox */}
                        <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                          {/* Action Buttons */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCustomer(customer);
                            }}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded transition-colors"
                            title={safeT('common.view', 'View')}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCustomer(customer);
                            }}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                            title={safeT('common.edit', 'Edit')}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomer(customer.CustomerID);
                            }}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title={safeT('common.delete', 'Delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          
                          {/* Selection Checkbox */}
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleCustomerSelect(customer.CustomerID)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-teal-600 dark:text-teal-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded cursor-pointer ml-1"
                            title="Select customer"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Card Body */}
                    <div className="p-4 space-y-2">
                      {/* Contact Info */}
                      {formatCustomerPhone(customer) !== '-' && (
                        <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-300">
                          <Phone className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                          <a 
                            href={`tel:${formatCustomerPhone(customer)}`}
                            className="truncate hover:text-teal-600 dark:hover:text-teal-400 hover:underline transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {formatCustomerPhone(customer)}
                          </a>
                        </div>
                      )}
                      
                      {formatCustomerContact(customer) !== '-' && formatCustomerContact(customer) !== formatCustomerPhone(customer) && (
                        <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-300">
                          {customer.CustomerType === 'Person' ? (
                            <>
                              <Mail className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                              {customer.PersonEmail && customer.PersonEmail !== 'null' ? (
                                renderMultipleEmails(customer.PersonEmail, 'truncate')
                              ) : (
                                <span className="truncate">{formatCustomerContact(customer)}</span>
                              )}
                            </>
                          ) : (
                            <>
                              <Globe className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                              {customer.Website && customer.Website !== 'null' ? (
                                <a 
                                  href={customer.Website.startsWith('http') ? customer.Website : `https://${customer.Website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="truncate hover:text-teal-600 dark:hover:text-teal-400 hover:underline transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {customer.Website}
                                </a>
                              ) : (
                                <span className="truncate">{formatCustomerContact(customer)}</span>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Company Link for Person */}
                      {customer.CustomerType === 'Person' && customer.PersonCompanyName && (
                        <div className="flex items-center space-x-2 text-xs">
                          <Building className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCompanyByCompanyId(customer.PersonCompanyID, customer.PersonCompanyName);
                            }}
                            className="text-teal-600 dark:text-teal-400 hover:underline truncate text-left"
                            title="View company details"
                          >
                            {customer.PersonCompanyName}
                          </button>
                        </div>
                      )}
                      
                      {/* Salesperson */}
                      {formatSalesPerson(customer) !== '-' && (
                        <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-300">
                          <Users className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                          <span className="truncate">{formatSalesPerson(customer)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Card Footer - Tags (Always at Bottom) */}
                    {customer.Tags && (() => {
                      // Parse tags - handle both string (comma-separated) and array formats
                      const tags = typeof customer.Tags === 'string' 
                        ? customer.Tags.split(',').map(t => t.trim()).filter(Boolean)
                        : Array.isArray(customer.Tags)
                        ? customer.Tags
                        : [];
                      
                      if (tags.length === 0) return null;
                      
                      // Helper function to get tag style (matching TagService component)
                      const getTagStyle = (tagName) => {
                        const tag = availableTags.find((t) => t.name === tagName);
                        const isDark = document.documentElement.classList.contains('dark');
                        
                        if (!tag) {
                          // Default style if tag not found in availableTags
                          return {
                            backgroundColor: isDark ? '#374151' : '#F3F4F6',
                            color: isDark ? '#E5E7EB' : '#374151',
                            borderColor: 'transparent',
                          };
                        }
                        return {
                          backgroundColor: tag.backgroundColor || (isDark ? '#374151' : '#F3F4F6'),
                          color: tag.textColor || (isDark ? '#E5E7EB' : '#374151'),
                          fontWeight: tag.fontWeight || 'normal',
                          borderColor: tag.borderColor || 'transparent',
                        };
                      };
                      
                      return (
                        <div className="p-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex flex-wrap items-center gap-2">
                            {tags.slice(0, 3).map((tag, idx) => {
                              const tagStyle = getTagStyle(tag);
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTag(tag);
                                    setCurrentPage(1);
                                    setShowFilters(true);
                                  }}
                                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border transition-all cursor-pointer hover:opacity-80"
                                  style={tagStyle}
                                  title={`Filter by ${tag}`}
                                >
                                  <Tag className="h-3 w-3" />
                                  {tag}
                                </button>
                              );
                            })}
                            {tags.length > 3 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                +{tags.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : viewMode === 'list' ? (
        <DynamicEntityTable
          entityName="customer"
          data={paginatedCustomers}
          loading={loading || configLoading}
          onView={handleViewCustomer}
          onEdit={handleEditCustomer}
          onDelete={handleDeleteCustomer}
          onSelect={handleCustomerSelect}
          selectedItems={selectedCustomers}
          selectAll={selectAll}
          onSelectAll={handleSelectAll}
          onRowDoubleClick={handleViewCustomer}
          customColumns={tableColumns || undefined}
          customRender={{
          // Custom rendering for combination name with avatar
          CombinationName: (customer) => (
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                  customer.CustomerType === 'Company' 
                    ? 'bg-teal-200 dark:bg-teal-700' 
                    : customer.parentCompany
                    ? 'bg-green-200 dark:bg-green-700'
                    : 'bg-gray-200 dark:bg-gray-600'
                }`}>
                  {customer.CustomerType === 'Company' ? (
                    <Building className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                  ) : (
                    <Users className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  )}
                </div>
              </div>
              <div className={`ml-4 ${customer.parentCompany ? 'ml-8' : ''}`}>
                <div 
                  className={`text-sm font-medium cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors ${
                    customer.CustomerType === 'Company'
                      ? 'text-blue-900 dark:text-blue-100' 
                      : 'text-gray-900 dark:text-white'
                  }`}
                  onClick={() => handleViewCustomer(customer)}
                  title="Click to view details"
                >
                  {formatCustomerName(customer)}
                  {customer.parentCompany && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      at {customer.parentCompany}
                    </span>
                  )}
                </div>
                <div className={`text-sm ${
                  customer.CustomerType === 'Company'
                    ? 'text-blue-700 dark:text-blue-300' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {formatCustomerInfo(customer)}
                </div>
              </div>
            </div>
          ),
          // Custom rendering for phone
          PersonPhone: (customer) => (
            <div className="flex items-center space-x-1">
              <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              {customer.PersonPhone && customer.PersonPhone !== 'null' ? (
                renderMultiplePhones(customer.PersonPhone)
              ) : (
                <span>-</span>
              )}
            </div>
          ),
          // Make company column clickable to open company detail
          PersonCompanyName: (customer) => (
            customer.PersonCompanyName ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewCompanyByCompanyId(customer.PersonCompanyID, customer.PersonCompanyName);
                }}
                className="text-teal-600 dark:text-teal-400 hover:underline"
                title="View company details"
              >
                {customer.PersonCompanyName}
              </button>
            ) : (
              <span>-</span>
            )
          ),
          CompanyName: (customer) => (
            customer.CompanyName ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // If this row is already a company customer, open directly; otherwise map by CompanyID
                  if (customer.CustomerType === 'Company' && customer.CustomerID) {
                    handleViewCustomer(customer);
                  } else {
                    handleViewCompanyByCompanyId(customer.CompanyID, customer.CompanyName);
                  }
                }}
                className="text-teal-600 dark:text-teal-400 hover:underline"
                title="View company details"
              >
                {customer.CompanyName}
              </button>
            ) : (
              <span>-</span>
            )
          ),
          // Custom rendering for salesperson
          SalespersonID: (customer) => (
            <div className="flex items-center space-x-1">
              <Users className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span>{formatSalesPerson(customer)}</span>
            </div>
          )
        }}
        refs={customerRefs.current}
        />
      ) : (
        /* Spreadsheet View */
        <SpreadsheetView
          entityName="customer"
          entityConfig={entityConfig}
          data={filteredCustomers}
          columns={spreadsheetColumns}
          loading={loading}
          onView={handleViewCustomer}
          onEdit={handleEditCustomer}
          onDelete={handleDeleteCustomer}
          onCellSave={async (customerId, field, value) => {
            const selectedRole = localStorage.getItem('selectedRole');
            const response = await fetch(`/api/customers/${customerId}/field`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
              },
              body: JSON.stringify({
                fieldName: field,
                fieldValue: value
              })
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.message || 'Failed to update customer');
            }

            const responseData = await response.json();

            // Update local state with backend data
            if (responseData.success && responseData.data) {
              setCustomers(prevCustomers => 
                prevCustomers.map(c => 
                  c.CustomerID === customerId ? responseData.data : c
                )
              );
              toast.success(responseData.message || 'Field updated successfully');
            }
          }}
          onSelect={handleCustomerSelect}
          selectedItems={selectedCustomers}
          selectAll={selectAll}
          onSelectAll={handleSelectAll}
          refs={customerRefs.current}
          getDisplayValue={getSpreadsheetCellValue}
          isFieldEditable={getEditableField}
          sortConfig={sortConfig}
          onSort={handleSort}
          i18n={i18n}
        />
      )}

      {/* Pagination - Mobile Optimized */}
      {totalPages > 1 && (
        <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center gap-3 sm:gap-4">
          <div className="flex items-center justify-center xs:justify-start space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white touch-target"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            <div className="flex space-x-1 overflow-x-auto max-w-[calc(100vw-200px)] xs:max-w-none">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                // On mobile, show current page, first, last, and adjacent pages
                const showOnMobile = page === 1 || page === totalPages || 
                  (page >= currentPage - 1 && page <= currentPage + 1);
                
                if (!showOnMobile && window.innerWidth < 640) {
                  // Show ellipsis for skipped pages
                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="px-2 py-2 text-gray-500 dark:text-gray-400 text-sm">
                        ...
                      </span>
                    );
                  }
                  return null;
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm touch-target ${
                      currentPage === page
                        ? 'bg-teal-600 dark:bg-teal-500 text-white font-medium'
                        : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white touch-target"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 text-center xs:text-right">
            <span className="hidden sm:inline">{safeT('customers.pageInfo', `Page ${currentPage} of ${totalPages}`, { current: currentPage, total: totalPages })}</span>
            <span className="sm:hidden">Page {currentPage} of {totalPages}</span>
          </p>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {safeT('customers.selectCustomersForExport', 'Select Customers for Export')}
              </h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-teal-600 dark:text-teal-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded mr-2"
                    />
                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                      {safeT('common.selectAll', 'Select All')}
                    </span>
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {selectedCustomers.size} of {paginatedCustomers.length} selected
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                {paginatedCustomers.map((customer) => (
                  <div key={customer.CustomerID} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.has(customer.CustomerID)}
                        onChange={() => handleCustomerSelect(customer.CustomerID)}
                        className="h-4 w-4 text-teal-600 dark:text-teal-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded mr-3"
                      />
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                          {formatCustomerName(customer)}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                          {customer.CustomerType === 'Person' ? safeT('customers.individual', 'Person') : safeT('customers.company', 'Organization')}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {formatCustomerPhone(customer)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {safeT('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleExportSelected}
                disabled={selectedCustomers.size === 0}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-teal-600 dark:bg-teal-500 rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>{safeT('customers.exportSelected', 'Export Selected')} ({selectedCustomers.size})</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Excel Import Modal */}
      {showExcelImport && (
        <ExcelImport
          entityName="customer"
          onClose={() => {
            setShowExcelImport(false);
            // Always refresh the customer list when closing the import modal
            // Use a small delay to ensure any pending operations complete
            setTimeout(() => {
              fetchCustomers();
              // Also refresh tab counts to update Person/Company counts
              fetchTabCounts();
            }, 300);
          }}
          onImportComplete={(results) => {
            toast.success(
              safeT('customers.importComplete', 'Import completed: {success} succeeded, {failed} failed', {
                success: results.success,
                failed: results.failed
              })
            );
            // Refresh the list and tab counts after successful import
            setTimeout(() => {
              fetchCustomers();
              fetchTabCounts();
            }, 300);
          }}
        />
      )}
      
      {/* Bulk Tag Manager Modal */}
      <BulkTagManager
        isOpen={showBulkTagManager}
        onClose={() => setShowBulkTagManager(false)}
        entityType="Customer"
        entityIds={Array.from(selectedCustomers)}
        entityCount={selectedCustomers.size}
        onSuccess={handleBulkTagSuccess}
      />
      
      {/* Bulk Field Update Modal */}
      <BulkFieldUpdateModal
        isOpen={showBulkFieldUpdate}
        onClose={() => setShowBulkFieldUpdate(false)}
        entityName="customer"
        entityIds={Array.from(selectedCustomers)}
        entityCount={selectedCustomers.size}
        customerType={activeTab === 'person' ? 'Person' : activeTab === 'company' ? 'Company' : null}
        onSuccess={handleBulkFieldUpdateSuccess}
      />
      
      {/* Dependency Delete Dialog */}
      <DependencyDeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, customerId: null, dependencies: null })}
        onConfirm={handleConfirmDeleteWithDependencies}
        entityName="customer"
        dependencies={deleteDialog.dependencies}
      />
    </div>
  );
};

export default Customers; 